import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr } from "@/lib/api";
import { imgSrc } from "@/lib/imgSrc";
import { MaskedLines } from "@/components/Motion";
import PasswordInput from "@/components/PasswordInput";
import { usePageMeta } from "@/lib/seo";

export default function Auth() {
  usePageMeta({ title: "Sign in or Join | Solis Casting", noindex: true });
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") === "employer" ? "employer" : "model");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const sendReset = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Enter your email first."); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      setForgotSent(true);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !ageConfirmed) { toast.error("Please confirm you are 18 or older first."); return; }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to the atelier.");
      } else {
        await register({ name: form.name, email: form.email, password: form.password, role, company: form.company || undefined, age_confirmed: ageConfirmed });
        toast.success(role === "model" ? "Welcome to the talent board." : "Welcome — your first casting awaits.");
      }
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/") ? next : "/dashboard");
    } catch (err) {
      toast.error(
        err?.response
          ? fmtErr(err)
          : "Connection hiccup — please try again. If you just created your account, switch to Sign In — it may have gone through."
      );
    } finally {
      setBusy(false);
    }
  };

  const [backdrop, setBackdrop] = useState(null);
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    api.get("/auth/backdrop").then((r) => { if (r.data?.url) setBackdrop(r.data); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-[85vh] grid grid-cols-1 lg:grid-cols-12 relative overflow-hidden" data-testid="auth-page">
      {backdrop && backdrop.kind === "video" && !reducedMotion && (
        <video
          data-testid="auth-background-video"
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
          src={imgSrc(backdrop.url)}
          autoPlay muted loop playsInline preload="metadata"
        />
      )}
      {backdrop && backdrop.kind === "image" && (
        <img
          data-testid="auth-background-image"
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
          src={imgSrc(backdrop.url)}
          alt=""
        />
      )}
      {backdrop && <div className="absolute inset-0 bg-background/40 pointer-events-none" aria-hidden="true" />}
      <div className="lg:col-span-6 flex flex-col justify-center px-6 sm:px-10 py-24 border-b lg:border-b-0 lg:border-r border-white/10 relative">
        <p className="overline-label mb-8">{mode === "login" ? "Backstage pass" : "Open registration"}</p>
        <MaskedLines
          lines={mode === "login" ? [<>STEP BACK</>, <>INTO THE <span className="italic text-accent">ROOM.</span></>] : [<>TAKE YOUR</>, <>PLACE ON THE <span className="italic text-accent">BOARD.</span></>]}
          className="font-display font-light text-5xl sm:text-7xl tracking-tighter leading-[0.95]"
        />
        <p className="mt-10 max-w-md text-muted-foreground leading-relaxed">
          {mode === "login"
            ? "Sign in to manage your book, track applications or review your casting calls."
            : "One account. Models build their book and apply; brands post calls and review applicants."}
        </p>
      </div>
      <div className="lg:col-span-6 flex items-center px-6 sm:px-10 py-24 relative">
        {forgot && mode === "login" ? (
          <div className="w-full max-w-md mx-auto" data-testid="forgot-password-panel">
            {forgotSent ? (
              <>
                <p className="overline-label mb-6 !text-accent">Check your inbox</p>
                <p className="font-display font-light text-3xl tracking-tight mb-4">A new key is on its way.</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-10">
                  If <strong className="text-white">{form.email}</strong> is registered, a reset link just landed — it works once, for one hour.
                </p>
                <button data-testid="forgot-back" onClick={() => { setForgot(false); setForgotSent(false); }} className="btn-outline w-full">Back to sign in</button>
              </>
            ) : (
              <form onSubmit={sendReset}>
                <p className="overline-label mb-6">Forgot your password?</p>
                <p className="font-display font-light text-3xl tracking-tight mb-4">We'll cut you a new key.</p>
                <p className="text-sm text-muted-foreground mb-8">Enter your account email and a reset link will be on its way.</p>
                <input data-testid="forgot-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="field mb-8" />
                <button data-testid="forgot-submit" disabled={busy} className="btn-primary w-full disabled:opacity-40 mb-4">
                  {busy ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" data-testid="forgot-cancel" onClick={() => setForgot(false)} className="btn-outline w-full">Back to sign in</button>
              </form>
            )}
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md mx-auto" data-testid="auth-form">
          <div className="flex gap-px bg-white/10 border border-white/10 mb-10">
            {["login", "register"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`auth-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-3 font-grotesk text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  mode === m ? "bg-primary text-primary-foreground" : "bg-background hover:bg-white/5"
                }`}
              >
                {m === "login" ? "Sign in" : "Register"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <>
              <div className="flex gap-px bg-white/10 border border-white/10 mb-10" data-testid="role-select">
                {[
                  { v: "model", label: "I'm a model" },
                  { v: "employer", label: "I'm a brand" },
                ].map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    data-testid={`role-${r.v}`}
                    onClick={() => setRole(r.v)}
                    className={`flex-1 py-3 font-grotesk text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      role === r.v ? "bg-accent text-accent-foreground" : "bg-background hover:bg-white/5"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <input data-testid="auth-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className="field mb-6" />
              {role === "employer" && (
                <input data-testid="auth-company-input" value={form.company} onChange={set("company")} placeholder="House / brand / agency" className="field mb-6" />
              )}
            </>
          )}

          <input data-testid="auth-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="field mb-6" />
          <PasswordInput data-testid="auth-password-input" required minLength={8} value={form.password} onChange={set("password")} placeholder="Password (8+ characters)" className={mode === "login" ? "field mb-3" : "field mb-10"} autoComplete={mode === "login" ? "current-password" : "new-password"} />
          {mode === "login" && (
            <div className="text-right mb-8">
              <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)}
                className="overline-label !text-[10px] !text-white/40 hover:!text-accent transition-colors">
                Forgot your password?
              </button>
            </div>
          )}

          {mode === "register" && (
            <label data-testid="age-consent-label" className="flex items-start gap-3 mb-8 cursor-pointer group">
              <input
                data-testid="age-consent-checkbox"
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-0.5 accent-[#C8102E]"
              />
              <span className="font-grotesk text-[11px] uppercase tracking-[0.12em] text-white/60 group-hover:text-white/90 transition-colors leading-relaxed">
                I confirm I am 18 or older <span className="text-accent">*</span>
              </span>
            </label>
          )}

          <button data-testid="auth-submit-button" disabled={busy} className="btn-primary w-full disabled:opacity-40">
            {busy ? "One moment…" : mode === "login" ? "Sign in" : role === "model" ? "Join the talent board" : "Open the atelier"}
          </button>

          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 border-t border-white/10" />
            <span className="overline-label !text-[10px]">or</span>
            <div className="flex-1 border-t border-white/10" />
          </div>

          <button type="button" data-testid="google-signin-button" onClick={googleLogin} className="btn-outline w-full">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </button>

          <p className="mt-8 text-center">
            <Link to="/" className="overline-label !text-[10px] hover:text-foreground transition-colors">Return to the runway</Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
